import {
  DashboardSummary,
  EmvTrendResponse,
  AccountEmvResponse,
  AssetTypeEmvResponse,
  AssetEmvResponse,
  TopTagsResponse,
  ActiveActivationsResponse,
  DashboardPeriod,
  DashboardSortBy,
  DashboardSortOrder
} from '../types/activationDashboard'

// API URL - using CRO Service
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_CRO_API_URL
  const mode = import.meta.env.MODE

  if (envUrl) {
    return envUrl
  }

  const API_URLS = {
    local: 'http://localhost:3007/api/v1',
    remote: 'https://ath-avi-cro-uat-west.azurewebsites.net/api/v1'
  }

  return mode === 'remote' ? API_URLS.remote : API_URLS.local
}

interface ApiResponse<T> {
  success: boolean
  data: T
  message?: string
}

class ActivationDashboardService {
  private baseURL: string

  constructor() {
    this.baseURL = getApiUrl()
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth-token')
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error?.message || errorData.message || `HTTP ${response.status}`
      )
    }
    return response.json()
  }

  private async fetchWithTimeout<T>(url: URL, timeoutMs = 30000): Promise<T> {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    return this.handleResponse<T>(response)
  }

  /**
   * Get dashboard summary with velocity metrics
   * @param customerId - Customer ID (required)
   * @param period - Time period ('day', 'week', 'month', '60days', '90days', 'custom')
   * @param customDateRange - Custom date range (required if period='custom')
   */
  async getDashboardSummary(
    customerId: string,
    period: DashboardPeriod = 'month',
    customDateRange?: { start: string; end: string } | null
  ): Promise<DashboardSummary> {
    const url = new URL(`${this.baseURL}/activations/dashboard/summary`)
    url.searchParams.append('customerId', customerId)
    url.searchParams.append('period', period)
    if (period === 'custom' && customDateRange) {
      url.searchParams.append('customStartDate', customDateRange.start)
      url.searchParams.append('customEndDate', customDateRange.end)
    }

    const response = await this.fetchWithTimeout<ApiResponse<DashboardSummary>>(url)
    return response.data
  }

  /**
   * Get EMV trend over time
   * @param customerId - Customer ID (required)
   * @param period - Time period ('day', 'week', 'month', '60days', '90days', 'custom')
   * @param customDateRange - Custom date range (required if period='custom')
   */
  async getEmvTrend(
    customerId: string,
    period: DashboardPeriod = 'month',
    customDateRange?: { start: string; end: string } | null
  ): Promise<EmvTrendResponse> {
    const url = new URL(`${this.baseURL}/activations/dashboard/emv-trend`)
    url.searchParams.append('customerId', customerId)
    url.searchParams.append('period', period)
    if (period === 'custom' && customDateRange) {
      url.searchParams.append('customStartDate', customDateRange.start)
      url.searchParams.append('customEndDate', customDateRange.end)
    }

    const response = await this.fetchWithTimeout<ApiResponse<EmvTrendResponse>>(url)
    return response.data
  }

  /**
   * Get EMV breakdown by account (only accounts linked to assets in active activations)
   * @param customerId - Customer ID (required)
   * @param period - Time period ('day', 'week', 'month', '60days', '90days', 'custom')
   * @param customDateRange - Custom date range (required if period='custom')
   */
  async getEmvByAccount(
    customerId: string,
    period: DashboardPeriod = 'month',
    customDateRange?: { start: string; end: string } | null
  ): Promise<AccountEmvResponse> {
    const url = new URL(`${this.baseURL}/activations/dashboard/emv-by-account`)
    url.searchParams.append('customerId', customerId)
    url.searchParams.append('period', period)
    if (period === 'custom' && customDateRange) {
      url.searchParams.append('customStartDate', customDateRange.start)
      url.searchParams.append('customEndDate', customDateRange.end)
    }

    const response = await this.fetchWithTimeout<ApiResponse<AccountEmvResponse>>(url)
    return response.data
  }

  /**
   * Get EMV breakdown by asset type/category
   * @param customerId - Customer ID (required)
   * @param period - Time period ('day', 'week', 'month', '60days', '90days', 'custom')
   * @param customDateRange - Custom date range (required if period='custom')
   */
  async getEmvByAssetType(
    customerId: string,
    period: DashboardPeriod = 'month',
    customDateRange?: { start: string; end: string } | null
  ): Promise<AssetTypeEmvResponse> {
    const url = new URL(`${this.baseURL}/activations/dashboard/emv-by-asset-type`)
    url.searchParams.append('customerId', customerId)
    url.searchParams.append('period', period)
    if (period === 'custom' && customDateRange) {
      url.searchParams.append('customStartDate', customDateRange.start)
      url.searchParams.append('customEndDate', customDateRange.end)
    }

    const response = await this.fetchWithTimeout<ApiResponse<AssetTypeEmvResponse>>(url)
    return response.data
  }

  /**
   * Get EMV breakdown by individual asset
   * @param customerId - Customer ID (required)
   * @param period - Time period ('day', 'week', 'month', '60days', '90days', 'custom')
   * @param customDateRange - Custom date range (required if period='custom')
   */
  async getEmvByAsset(
    customerId: string,
    period: DashboardPeriod = 'month',
    customDateRange?: { start: string; end: string } | null
  ): Promise<AssetEmvResponse> {
    const url = new URL(`${this.baseURL}/activations/dashboard/emv-by-asset`)
    url.searchParams.append('customerId', customerId)
    url.searchParams.append('period', period)
    if (period === 'custom' && customDateRange) {
      url.searchParams.append('customStartDate', customDateRange.start)
      url.searchParams.append('customEndDate', customDateRange.end)
    }

    const response = await this.fetchWithTimeout<ApiResponse<AssetEmvResponse>>(url)
    return response.data
  }

  /**
   * Get top attribution tags with trends
   * @param customerId - Customer ID (required)
   * @param period - Time period ('day', 'week', 'month', '60days', '90days', 'custom')
   * @param limit - Number of tags to return (default: 5, max: 20)
   * @param customDateRange - Custom date range (required if period='custom')
   */
  async getTopTags(
    customerId: string,
    period: DashboardPeriod = 'month',
    limit: number = 5,
    customDateRange?: { start: string; end: string } | null
  ): Promise<TopTagsResponse> {
    const url = new URL(`${this.baseURL}/activations/dashboard/top-tags`)
    url.searchParams.append('customerId', customerId)
    url.searchParams.append('period', period)
    url.searchParams.append('limit', String(limit))
    if (period === 'custom' && customDateRange) {
      url.searchParams.append('customStartDate', customDateRange.start)
      url.searchParams.append('customEndDate', customDateRange.end)
    }

    const response = await this.fetchWithTimeout<ApiResponse<TopTagsResponse>>(url)
    return response.data
  }

  /**
   * Get active activations with computed status
   * @param customerId - Customer ID (required)
   * @param sortBy - Sort field ('status', 'emv', 'name', 'daysRemaining')
   * @param sortOrder - Sort order ('asc', 'desc')
   */
  async getActiveActivations(
    customerId: string,
    sortBy: DashboardSortBy = 'status',
    sortOrder: DashboardSortOrder = 'desc'
  ): Promise<ActiveActivationsResponse> {
    const url = new URL(`${this.baseURL}/activations/dashboard/active`)
    url.searchParams.append('customerId', customerId)
    url.searchParams.append('sortBy', sortBy)
    url.searchParams.append('sortOrder', sortOrder)

    const response = await this.fetchWithTimeout<ApiResponse<ActiveActivationsResponse>>(url)
    return response.data
  }

  /**
   * Fetch all dashboard data in parallel
   * @param customerId - Customer ID (required)
   * @param period - Time period ('day', 'week', 'month', '60days', '90days', 'custom')
   * @param customDateRange - Custom date range (required if period='custom')
   */
  async getAllDashboardData(
    customerId: string,
    period: DashboardPeriod = 'month',
    customDateRange?: { start: string; end: string } | null
  ): Promise<{
    summary: DashboardSummary
    emvTrend: EmvTrendResponse
    accountEmv: AccountEmvResponse
    assetTypeEmv: AssetTypeEmvResponse
    assetEmv: AssetEmvResponse
    topTags: TopTagsResponse
    activeActivations: ActiveActivationsResponse
  }> {
    const [summary, emvTrend, accountEmv, assetTypeEmv, assetEmv, topTags, activeActivations] =
      await Promise.all([
        this.getDashboardSummary(customerId, period, customDateRange),
        this.getEmvTrend(customerId, period, customDateRange),
        this.getEmvByAccount(customerId, period, customDateRange),
        this.getEmvByAssetType(customerId, period, customDateRange),
        this.getEmvByAsset(customerId, period, customDateRange),
        this.getTopTags(customerId, period, 5, customDateRange),
        this.getActiveActivations(customerId)
      ])

    return {
      summary,
      emvTrend,
      accountEmv,
      assetTypeEmv,
      assetEmv,
      topTags,
      activeActivations
    }
  }
}

export const activationDashboardService = new ActivationDashboardService()
